import { useEffect, useId, useState, type FormEvent, type ReactNode } from "react";
import {
  useCreateAgentSession,
  useCreateEnrollToken,
  useCreateLlmCredential,
  useEnvironments,
  useLlmCredentials,
  useNodes,
} from "../../api/queries";
import type { AgentSession, LlmProvider } from "../../api/types";
import { Button } from "../../components/Button";
import { CopyButton } from "../../components/CopyButton";
import { FormError } from "../../components/FormError";
import { Modal } from "../../components/Modal";
import { Spinner } from "../../components/AsyncStates";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";
import "../nodes/add-host.css";
import "./launch-wizard.css";

interface LaunchWorkbenchWizardProps {
  open: boolean;
  onClose: () => void;
}

type StepId = "credential" | "node" | "session" | "success";

interface StepDef {
  id: StepId;
  label: string;
  needed: boolean;
}

export function LaunchWorkbenchWizard({ open, onClose }: LaunchWorkbenchWizardProps) {
  const credentials = useLlmCredentials();
  const nodes = useNodes();
  const { pushToast } = useToast();

  const hasCredentials = (credentials.data?.items.length ?? 0) > 0;
  const hasNodes = (nodes.data?.items.length ?? 0) > 0;
  const loading = credentials.isPending || nodes.isPending;

  const steps: StepDef[] = [
    { id: "credential", label: "LLM credential", needed: !hasCredentials },
    { id: "node", label: "Enroll a host", needed: !hasNodes },
    { id: "session", label: "Launch session", needed: true },
  ];

  const firstNeeded = steps.find((s) => s.needed)?.id ?? "session";
  const [currentStep, setCurrentStep] = useState<StepId>(firstNeeded);
  // The just-created session, carrying its one-time token — set only by
  // SessionStep's onSuccess, cleared whenever the wizard closes so a token
  // never survives past the modal that showed it (S-13's "transient UI
  // state only" spirit).
  const [createdSession, setCreatedSession] = useState<AgentSession | null>(null);

  useEffect(() => {
    if (open && !loading && currentStep !== "success") {
      setCurrentStep(steps.find((s) => s.needed)?.id ?? "session");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loading, hasCredentials, hasNodes]);

  function handleClose() {
    setCurrentStep("credential");
    setCreatedSession(null);
    onClose();
  }

  function advanceFromCredential() {
    credentials.refetch();
    if (!hasNodes) {
      setCurrentStep("node");
    } else {
      setCurrentStep("session");
    }
  }

  function advanceFromNode() {
    nodes.refetch();
    setCurrentStep("session");
  }

  function stepStatus(step: StepDef): "done" | "active" | "skip" | "upcoming" {
    if (step.id === currentStep) return "active";
    const currentIdx = steps.findIndex((s) => s.id === currentStep);
    const stepIdx = steps.findIndex((s) => s.id === step.id);
    if (stepIdx < currentIdx) return !step.needed ? "skip" : "done";
    return !step.needed ? "skip" : "upcoming";
  }

  return (
    <Modal open={open} title="Launch a workbench session" onClose={handleClose} size="lg">
      {loading ? (
        <div style={{ padding: "var(--space-6) 0", textAlign: "center" }}>
          <Spinner label="Checking prerequisites…" />
        </div>
      ) : (
        <>
          {currentStep !== "success" && (
            <ol className="wizard-steps">
              {steps.map((step) => {
                const status = stepStatus(step);
                return (
                  <li key={step.id} className={`wizard-step wizard-step--${status}`}>
                    {step.label}
                  </li>
                );
              })}
            </ol>
          )}

          <div className="wizard-body">
            {currentStep === "credential" && (
              <CredentialStep
                onDone={advanceFromCredential}
                onCancel={handleClose}
              />
            )}
            {currentStep === "node" && (
              <NodeStep
                onDone={advanceFromNode}
                onBack={() => setCurrentStep("credential")}
                onCancel={handleClose}
                canGoBack={!hasCredentials}
              />
            )}
            {currentStep === "session" && (
              <SessionStep
                onDone={(session) => {
                  setCreatedSession(session);
                  setCurrentStep("success");
                }}
                onCancel={handleClose}
              />
            )}
            {currentStep === "success" && createdSession && (
              <SuccessStep
                session={createdSession}
                onDone={() => {
                  pushToast({ title: "Session launched.", tone: "success" });
                  handleClose();
                }}
              />
            )}
          </div>
        </>
      )}
    </Modal>
  );
}

/* ── Step 1: Add an LLM credential ─────────────────────────── */

function CredentialStep({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel: () => void;
}) {
  const providerId = useId();
  const labelId = useId();
  const apiKeyId = useId();
  const mutation = useCreateLlmCredential();
  const [provider, setProvider] = useState<LlmProvider>("gemini");
  const [label, setLabel] = useState("");
  const [apiKey, setApiKey] = useState("");

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    mutation.mutate(
      { provider, label: label.trim(), key: apiKey },
      { onSuccess: () => onDone() },
    );
    setApiKey("");
  }

  return (
    <div className="wizard-prereq">
      <h3 className="wizard-prereq__heading">Add an LLM credential</h3>
      <p className="wizard-prereq__desc">
        The workbench needs an API key to call your LLM provider. It's sealed on write and never shown again.
      </p>
      <form onSubmit={handleSubmit} noValidate>
        <label className="form-field" htmlFor={providerId}>
          <span>Provider</span>
          <select id={providerId} value={provider} onChange={(e) => setProvider(e.target.value as LlmProvider)}>
            <option value="gemini">Google Gemini (free tier)</option>
            <option value="openrouter">OpenRouter (one key, many models)</option>
            <option value="openai">OpenAI</option>
            <option value="deepseek">DeepSeek</option>
            <option value="ollama">Ollama (local, no key)</option>
          </select>
        </label>

        <label className="form-field" htmlFor={labelId}>
          <span>Label</span>
          <input
            id={labelId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            maxLength={128}
            placeholder="e.g. My Gemini key"
          />
        </label>

        <label className="form-field" htmlFor={apiKeyId}>
          <span>API key</span>
          <input
            id={apiKeyId}
            type="password"
            autoComplete="off"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            required
          />
        </label>

        {mutation.isError && (
          <FormError>{mutation.error instanceof Error ? mutation.error.message : "Failed to add credential."}</FormError>
        )}

        <WizardNav onCancel={onCancel}>
          <Button type="submit" variant="primary" disabled={mutation.isPending || !label.trim() || !apiKey}>
            {mutation.isPending ? "Adding…" : "Add & continue"}
          </Button>
        </WizardNav>
      </form>
    </div>
  );
}

/* ── Step 2: Enroll a host ──────────────────────────────────── */

function NodeStep({
  onDone,
  onBack,
  onCancel,
  canGoBack,
}: {
  onDone: () => void;
  onBack: () => void;
  onCancel: () => void;
  canGoBack: boolean;
}) {
  const environmentFieldId = useId();
  const [environmentName, setEnvironmentName] = useState("");
  const { data: environmentsPage, isPending: environmentsPending } = useEnvironments({ limit: 100 });
  const environments = environmentsPage?.items ?? [];
  const mintMutation = useCreateEnrollToken();
  const nodes = useNodes();

  // No default selection (PR #634 security audit condition 2): environments
  // sort by name, so environments[0] can be production-class in some
  // tenants — auto-selecting it would silently mint a join token against
  // production before the operator chose anything. The select's placeholder
  // stays selected until the operator explicitly names one, then mints;
  // re-mints if they pick a different environment.
  useEffect(() => {
    if (environmentName) {
      mintMutation.mutate({ environment: environmentName });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environmentName]);

  // Poll for a node appearing after the user runs the enroll command.
  useEffect(() => {
    const interval = setInterval(() => {
      nodes.refetch().then((result) => {
        if ((result.data?.items.length ?? 0) > 0) {
          clearInterval(interval);
          onDone();
        }
      });
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const origin = window.location.origin;
  const token = mintMutation.data?.token;
  const oneLiner = token ? `curl -fsSL ${origin}/install.sh | COMEBYE_TOKEN=${token} sh` : "";
  const advanced = token ? `kelpie -server ${origin} -token ${token}` : "";

  return (
    <div className="wizard-prereq">
      <h3 className="wizard-prereq__heading">Enroll a host</h3>
      <p className="wizard-prereq__desc">
        Run this on the machine where you want the workbench container to run. Kelpie connects outbound only — nothing to open on the host.
      </p>

      <label className="form-field" htmlFor={environmentFieldId}>
        <span>Environment</span>
        <select
          id={environmentFieldId}
          value={environmentName}
          onChange={(e) => setEnvironmentName(e.target.value)}
          required
          disabled={environmentsPending}
        >
          <option value="" disabled>
            {environmentsPending ? "Loading environments…" : "Choose an environment…"}
          </option>
          {environments.map((env) => (
            <option key={env.id} value={env.name}>
              {env.name} ({env.class})
            </option>
          ))}
        </select>
      </label>

      {mintMutation.isPending && <Spinner label="Minting a join token…" />}

      {mintMutation.isError && (
        <div>
          <FormError>
            {mintMutation.error instanceof Error ? mintMutation.error.message : "Couldn't mint a token."}
          </FormError>
          <Button variant="secondary" onClick={() => mintMutation.mutate({ environment: environmentName })}>
            Try again
          </Button>
        </div>
      )}

      {token && (
        <>
          <div className="add-host__command" style={{ marginBottom: "var(--space-3)" }}>
            <label className="add-host__label" htmlFor="wizard-enroll-cmd">One-liner</label>
            <code id="wizard-enroll-cmd" className="add-host__code">{oneLiner}</code>
            <CopyButton getValue={() => oneLiner} label="Copy" />
          </div>

          <details>
            <summary style={{ cursor: "pointer", fontSize: "var(--text-sm, 0.8125rem)", color: "var(--color-text-secondary)" }}>
              Manual / advanced
            </summary>
            <div className="add-host__command" style={{ marginTop: "var(--space-2)" }}>
              <code className="add-host__code">{advanced}</code>
              <CopyButton getValue={() => advanced} label="Copy" />
            </div>
          </details>

          <p style={{ fontSize: "var(--text-sm, 0.8125rem)", color: "var(--color-text-secondary)", marginTop: "var(--space-3)" }}>
            Waiting for a host to enroll… This step will advance automatically.
          </p>
        </>
      )}

      <WizardNav onCancel={onCancel}>
        {canGoBack && (
          <Button type="button" variant="secondary" onClick={onBack}>
            Back
          </Button>
        )}
        <Button type="button" variant="primary" onClick={onDone}>
          Skip — I'll enroll later
        </Button>
      </WizardNav>
    </div>
  );
}

/* ── Step 3: Create the session ─────────────────────────────── */

function SessionStep({
  onDone,
  onCancel,
}: {
  onDone: (session: AgentSession) => void;
  onCancel: () => void;
}) {
  const labelId = useId();
  const imageId = useId();
  const credentialId = useId();
  const mutation = useCreateAgentSession();
  const { data: credentials } = useLlmCredentials();
  const [label, setLabel] = useState("");
  const [image, setImage] = useState("ghcr.io/comebye/comebye-agent-task:latest");
  const [selectedCredential, setSelectedCredential] = useState("");

  const credentialItems = credentials?.items ?? [];

  // Auto-select the only credential if there's exactly one.
  useEffect(() => {
    if (credentialItems.length === 1 && !selectedCredential) {
      setSelectedCredential(credentialItems[0].id);
    }
  }, [credentialItems, selectedCredential]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    mutation.mutate(
      {
        credentialId: selectedCredential,
        image: image.trim(),
        label: label.trim(),
      },
      { onSuccess: (session) => onDone(session) },
    );
  }

  return (
    <div>
      <form onSubmit={handleSubmit} noValidate>
        <label className="form-field" htmlFor={labelId}>
          <span>Session label</span>
          <input
            id={labelId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            maxLength={128}
            placeholder="e.g. Backend refactor"
          />
        </label>

        <label className="form-field" htmlFor={credentialId}>
          <span>LLM credential</span>
          <select
            id={credentialId}
            value={selectedCredential}
            onChange={(e) => setSelectedCredential(e.target.value)}
            required
          >
            <option value="" disabled>
              Select a credential…
            </option>
            {credentialItems.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label} ({c.provider})
              </option>
            ))}
          </select>
        </label>

        <label className="form-field" htmlFor={imageId}>
          <span>Container image</span>
          <input
            id={imageId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={image}
            onChange={(e) => setImage(e.target.value)}
            required
            maxLength={512}
          />
          <span className="form-field__hint">Docker image for the coding agent container.</span>
        </label>

        {mutation.isError && (
          <FormError>{mutation.error instanceof Error ? mutation.error.message : "Failed to launch session."}</FormError>
        )}

        <WizardNav onCancel={onCancel}>
          <Button
            type="submit"
            variant="primary"
            disabled={mutation.isPending || !label.trim() || !selectedCredential || !image.trim()}
          >
            {mutation.isPending ? "Launching…" : "Launch session"}
          </Button>
        </WizardNav>
      </form>
    </div>
  );
}

/* ── Step 4: One-time session token display ───────────────── */

function SuccessStep({ session, onDone }: { session: AgentSession; onDone: () => void }) {
  const token = session.token ?? "";

  return (
    <div className="wizard-prereq">
      <h3 className="wizard-prereq__heading">Session launched</h3>
      <p className="wizard-prereq__desc">
        “{session.label}” is starting up. Here is its one-time session token — the server will not
        show it again, so copy it now if the container needs it outside its own environment.
      </p>

      {token ? (
        <div className="add-host__command" style={{ marginBottom: "var(--space-3)" }}>
          <label className="add-host__label" htmlFor="wizard-session-token">
            Session token (shown once)
          </label>
          <code id="wizard-session-token" className="add-host__code">
            {token}
          </code>
          <CopyButton getValue={() => token} label="Copy" />
        </div>
      ) : (
        <FormError>
          No token was returned for this session — the container will need one minted another way.
        </FormError>
      )}

      <p style={{ fontSize: "var(--text-sm, 0.8125rem)", color: "var(--color-text-secondary)" }}>
        The workbench container reads this from two environment variables Comebye sets for it:
      </p>
      <ul style={{ fontSize: "var(--text-sm, 0.8125rem)", color: "var(--color-text-secondary)", marginTop: 0 }}>
        <li>
          <code>COMEBYE_SESSION_TOKEN</code> — this token, used to authenticate back to the control
          plane.
        </li>
        <li>
          <code>COMEBYE_LLM_PROXY_URL</code> — where the container sends its LLM API calls, so the
          proxy can authorize them on the session's behalf.
        </li>
      </ul>

      <div className="wizard-nav">
        <div className="wizard-nav__end">
          <Button type="button" variant="primary" onClick={onDone}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ── Shared wizard navigation bar ───────────────────────────── */

function WizardNav({ onCancel, children }: { onCancel: () => void; children: ReactNode }) {
  return (
    <div className="wizard-nav">
      <Button type="button" variant="secondary" onClick={onCancel}>
        Cancel
      </Button>
      <div className="wizard-nav__end">{children}</div>
    </div>
  );
}
